import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { eachDayOfInterval } from 'date-fns/eachDayOfInterval';
import { endOfMonth } from 'date-fns/endOfMonth';
import { format } from 'date-fns/format';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfWeek } from 'date-fns/startOfWeek';
import { subMonths } from 'date-fns/subMonths';

import type {
  PeriodoIntervalo,
  PeriodoPresetKey,
  PeriodoValor,
} from '@/shared/types/filtroPeriodo';

export const PERIODO_PRESETS: ReadonlyArray<{ key: PeriodoPresetKey; label: string }> = [
  { key: 'hoje', label: 'Hoje' },
  { key: '7d', label: 'Últimos 7 dias' },
  { key: '30d', label: 'Últimos 30 dias' },
  { key: 'mes', label: 'Este mês' },
  { key: 'mesPassado', label: 'Mês passado' },
  { key: 'custom', label: 'Personalizado' },
];

const PERIODO_MESES_PT = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

export const PERIODO_DIAS_SEMANA_PT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;

export const PERIODO_VALOR_VAZIO: PeriodoValor = {
  campo: '',
  inicio: null,
  fim: null,
  preset: null,
};

/** Converte um `Date` local para a string ISO de data pura (`yyyy-MM-dd`) usada em todo o filtro. */
export function paraIsoData(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** `new Date(2026, 1, 31)` rola para 03/03 em vez de falhar, então só a volta dos campos prova que o dia existe. */
function dataLocalExistente(ano: number, mes: number, dia: number): Date | null {
  const date = new Date(ano, mes - 1, dia);
  const existe =
    date.getFullYear() === ano && date.getMonth() === mes - 1 && date.getDate() === dia;
  return existe ? date : null;
}

/**
 * Lê `yyyy-MM-dd` como data local. `new Date(iso)` interpretaria a string como UTC e
 * deslocaria o dia em fusos negativos (o Brasil inteiro), quebrando a seleção do calendário.
 */
export function deIsoData(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!partes) return null;
  return dataLocalExistente(Number(partes[1]), Number(partes[2]), Number(partes[3]));
}

/** `2026-09-10` → `10/09/2026`. Devolve string vazia para valor ausente. */
export function isoParaBr(iso: string | null | undefined): string {
  const date = deIsoData(iso);
  return date ? format(date, 'dd/MM/yyyy') : '';
}

/** `10/09/2026` → `2026-09-10`. Devolve `null` enquanto o usuário ainda está digitando. */
export function brParaIso(texto: string | null | undefined): string | null {
  const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((texto ?? '').trim());
  if (!partes) return null;
  const date = dataLocalExistente(Number(partes[3]), Number(partes[2]), Number(partes[1]));
  return date ? paraIsoData(date) : null;
}

/** Máscara de data no padrão brasileiro: `10092026` → `10/09/2026`. */
export function maskDateBr(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('/');
}

export function intervaloDoPreset(
  preset: PeriodoPresetKey,
  hoje: Date = new Date(),
): PeriodoIntervalo {
  switch (preset) {
    case 'hoje':
      return { inicio: paraIsoData(hoje), fim: paraIsoData(hoje) };
    case '7d':
      return { inicio: paraIsoData(addDays(hoje, -6)), fim: paraIsoData(hoje) };
    case '30d':
      return { inicio: paraIsoData(addDays(hoje, -29)), fim: paraIsoData(hoje) };
    case 'mes':
      return { inicio: paraIsoData(startOfMonth(hoje)), fim: paraIsoData(endOfMonth(hoje)) };
    case 'mesPassado':
      return {
        inicio: paraIsoData(startOfMonth(subMonths(hoje, 1))),
        fim: paraIsoData(endOfMonth(subMonths(hoje, 1))),
      };
    default:
      return { inicio: null, fim: null };
  }
}

/**
 * As 6 semanas (42 dias) que o calendário mensal renderiza, começando no domingo.
 * São sempre 42 — meses de 5 semanas completam a última linha para o popover não mudar de altura
 * ao navegar entre meses.
 */
export function gradeDoMes(mes: Date): Date[] {
  const primeiro = startOfWeek(startOfMonth(mes), { weekStartsOn: 0 });
  return eachDayOfInterval({ start: primeiro, end: addDays(primeiro, 41) });
}

export function deslocarMes(mes: Date, passos: number): Date {
  return startOfMonth(addMonths(mes, passos));
}

export function rotuloDoMes(mes: Date): string {
  return `${PERIODO_MESES_PT[mes.getMonth()]} de ${mes.getFullYear()}`;
}

/** `10/09/2026 – 15/09/2026`, ou só a data quando o intervalo é de um dia. */
export function rotuloDoIntervalo({ inicio, fim }: PeriodoIntervalo): string {
  if (!inicio) return '';
  if (!fim || fim === inicio) return isoParaBr(inicio);
  return `${isoParaBr(inicio)} – ${isoParaBr(fim)}`;
}

export function rotuloDoPreset(preset: PeriodoPresetKey | null): string | null {
  if (!preset || preset === 'custom') return null;
  return PERIODO_PRESETS.find((item) => item.key === preset)?.label ?? null;
}

export function periodoEstaAtivo(valor: PeriodoValor): boolean {
  return Boolean(valor.inicio);
}
